import React from "react";
import { MdCheck, MdExpandMore, MdOpenInNew } from "react-icons/md";
import { useAuth } from "contexts/AuthContext";
import { DropdownMenu, MenuLabel, MenuLinkItem, MenuSeparator, menuItemClass } from "components/ui/DropdownMenu";
import { Toast, ToastViewport } from "components/ui/Toast";
import {
  ADMIN_PORTAL_URL,
  groupSeatsByOrganisation,
  roleTheme,
  seatFromPortal,
  seatKey,
  seatOrganisation,
} from "lib/seats";

/** Kuest badge, outline variant. */
export function ReadOnlyChip({ className = "" }) {
  return (
    <span
      className={`inline-flex shrink-0 items-center rounded-md border border-border px-1.5 py-0.5 text-[10px] font-medium leading-none text-muted-foreground ${className}`}
    >
      Read only
    </span>
  );
}

/** The roles you hold, grouped by organisation, as menu radio items. */
function SeatList({ seats, currentKey, busyKey, onPick }) {
  const groups = groupSeatsByOrganisation(seats);
  return (
    <>
      {groups.map((group, groupIndex) => (
        <div key={group.tenantId} role="group" aria-label={group.tenantName}>
          {groupIndex > 0 && <MenuSeparator />}
          <p className="px-2 pb-1 pt-1.5 text-xs text-muted-foreground">{group.tenantName}</p>
          {group.seats.map((seat) => {
            const key = seatKey(seat);
            const active = seat.active || key === currentKey;
            const theme = roleTheme(seat.kind);
            return (
              <button
                key={key}
                type="button"
                role="menuitemradio"
                aria-checked={active}
                tabIndex={-1}
                disabled={Boolean(busyKey)}
                onClick={() => onPick(seat, active)}
                className={`${menuItemClass} pr-8 disabled:cursor-wait`}
              >
                <span className={`h-2 w-2 shrink-0 rounded-full ${theme.dot}`} aria-hidden="true" />
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-1.5">
                    <span className="truncate font-medium">{seat.roleLabel}</span>
                    {!seat.canManage && <ReadOnlyChip />}
                  </span>
                  <span className="block truncate text-xs text-muted-foreground">
                    {seat.kind === "PARTNER" ? seat.partnerName : seat.tenantName}
                  </span>
                </span>
                {busyKey === key ? (
                  <span className="shrink-0 text-xs text-muted-foreground">Switching…</span>
                ) : active ? (
                  <span className="pointer-events-none absolute right-2 flex h-4 w-4 items-center justify-center">
                    <MdCheck className="h-4 w-4 !text-foreground" aria-label="Current role" />
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      ))}
    </>
  );
}

export default function RoleSwitcher({ variant = "navbar" }) {
  const { portal, seats, platformAdmin, switchSeat } = useAuth();
  const [open, setOpen] = React.useState(false);
  const [busyKey, setBusyKey] = React.useState(null);
  const [error, setError] = React.useState(null);

  const current = seatFromPortal(portal);
  if (!current) return null;

  const theme = roleTheme(current.kind);
  const organisation = seatOrganisation(current);
  const seatCount = Math.max(portal?.seatCount || 0, seats.length);
  const switchable = seatCount > 1 && seats.length > 1;
  const interactive = switchable || platformAdmin;
  const currentKey = seatKey(current);

  async function pick(seat, active) {
    if (active) {
      setOpen(false);
      return;
    }
    setError(null);
    setBusyKey(seatKey(seat));
    try {
      await switchSeat(seat);
    } catch (err) {
      setBusyKey(null);
      setError(err?.code === "SEAT_NOT_HELD" ? "You no longer hold that role." : err?.message || "Could not switch role.");
    }
  }

  const isSidebar = variant === "sidebar";

  const badgeInner = (chevronOpen) => (
    <>
      <span className={`h-2 w-2 shrink-0 rounded-full ${theme.dot}`} aria-hidden="true" />
      <span className={`min-w-0 ${isSidebar ? "flex-1" : ""}`}>
        {isSidebar ? (
          <>
            <span className="block truncate text-xs text-muted-foreground">{current.roleLabel}</span>
            <span className="block truncate text-sm font-semibold text-foreground" title={organisation}>
              {organisation}
            </span>
          </>
        ) : (
          <span className="block truncate text-sm font-medium text-foreground" title={`${current.roleLabel} · ${organisation}`}>
            <span className={theme.text}>{current.roleLabel}</span>
            <span className="text-muted-foreground"> · </span>
            {organisation}
          </span>
        )}
      </span>
      {!current.canManage && <ReadOnlyChip />}
      {interactive && (
        <MdExpandMore className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-150 ${chevronOpen ? "rotate-180" : ""}`} aria-hidden="true" />
      )}
    </>
  );

  // Kuest ghost trigger (navbar) / outline card trigger (drawer).
  const badgeClass = isSidebar
    ? "flex w-full items-center gap-2.5 rounded-md border border-border bg-background px-3 py-2 text-left"
    : "flex h-9 max-w-full items-center gap-2 rounded-sm px-2.5 text-left";

  if (!interactive) {
    return (
      <div className={`${isSidebar ? "w-full" : "inline-flex max-w-full"}`}>
        <div className={badgeClass}>{badgeInner(false)}</div>
      </div>
    );
  }

  return (
    <DropdownMenu
      open={open}
      onOpenChange={setOpen}
      align={isSidebar ? "start" : "end"}
      label="Switch role"
      wrapperClassName={isSidebar ? "w-full" : "max-w-full"}
      className={isSidebar ? "w-full" : "w-80"}
      trigger={({ open: isOpen, props }) => (
        <button
          type="button"
          {...props}
          title={switchable ? "Switch role" : undefined}
          className={`${badgeClass} transition-colors hover:bg-accent/70 data-[state=open]:bg-accent/70`}
        >
          {badgeInner(isOpen)}
        </button>
      )}
    >
      {switchable && (
        <>
          <MenuLabel>Switch role</MenuLabel>
          <SeatList seats={seats} currentKey={currentKey} busyKey={busyKey} onPick={pick} />
        </>
      )}
      {error && <p className="px-2 py-1.5 text-xs font-medium text-destructive">{error}</p>}
      {platformAdmin && (
        <>
          {switchable && <MenuSeparator />}
          <MenuLinkItem href={ADMIN_PORTAL_URL} className="font-medium">
            <MdOpenInNew className="h-4 w-4 !text-current" aria-hidden="true" /> Open admin portal
          </MenuLinkItem>
        </>
      )}
    </DropdownMenu>
  );
}

export function RoleAccentBar() {
  const { portal } = useAuth();
  if (!portal?.kind) return null;
  return <div className={`fixed inset-x-0 top-0 z-[60] h-1 ${roleTheme(portal.kind).bar}`} aria-hidden="true" />;
}

/** Role-switch and wrong-role notices, as a Kuest toast (bottom-right). */
export function RoleNotice({ notice, onDismiss }) {
  React.useEffect(() => {
    if (!notice) return undefined;
    const id = setTimeout(onDismiss, 7000);
    return () => clearTimeout(id);
  }, [notice, onDismiss]);
  if (!notice) return null;
  const theme = notice.kind ? roleTheme(notice.kind) : null;
  return (
    <ToastViewport>
      <Toast
        type={theme ? "success" : "warning"}
        title={notice.text}
        media={theme ? <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${theme.dot}`} aria-hidden="true" /> : undefined}
        onDismiss={onDismiss}
      />
    </ToastViewport>
  );
}
